<template>
  <div id="segments-page">
    <div class="container-fluid">
      <div class="segments-form-container">
        <loader v-if="is('loading')"></loader>
        <form @submit.prevent v-if="is('ready')">
          <h1 class="page-title">{{ t('segments-form-add_title') }}</h1>

          <div class="segment-card">
            <div class="field segment-name">
              <small>{{ t('segments-form-name_helper') }}</small>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text"
                    ><i class="fas fa-tag"></i
                  ></span>
                </div>
                <input
                  type="text"
                  class="form-control"
                  v-model="form.name"
                  :placeholder="t('segments-form-name_placeholder')"
                  id="segment-name"
                  name="segment-name"
                />
              </div>
            </div>

            <div class="daily-update field mt-4">
              <small>{{ t('segments-form-update_daily') }}</small>
              <small class="d-block text-muted mb-2">{{ t('segments-form-type_help') }}</small>
              <small class="d-flex justify-content-start">
                <label class="ml-2 w-auto" for="updateDaily">
                  <input
                    class="inline"
                    type="radio"
                    name="updateDaily"
                    :value="true"
                    v-model="isDynamic"
                    id="updateDaily"
                  />
                  {{ t('segments-form-yes') }}
                </label>
                <label class="ml-2 w-auto" for="updateDaily2">
                  <input
                    class="inline"
                    type="radio"
                    name="updateDaily"
                    id="updateDaily2"
                    :value="false"
                    v-model="isDynamic"
                  />
                    {{ t('segments-form-no') }}
                </label>
              </small>
            </div>

            <div
              class="manual-populate field mt-4"
              v-if="form.type === 'manual' && !id"
            >
              <small>{{ t('segments-form-manual_populate_label') }}</small>
              <small class="d-flex justify-content-start">
                <label class="ml-2 w-auto" for="startEmpty">
                  <input
                    class="inline"
                    type="radio"
                    name="manualPopulate"
                    id="startEmpty"
                    :value="false"
                    v-model="populateFromFilters"
                  />
                  {{ t('segments-form-start_empty') }}
                </label>
                <label class="ml-2 w-auto" for="populateFromFilters">
                  <input
                    class="inline"
                    type="radio"
                    name="manualPopulate"
                    id="populateFromFilters"
                    :value="true"
                    v-model="populateFromFilters"
                  />
                  {{ t('segments-form-populate_from_filters') }}
                </label>
              </small>
            </div>
          </div>

          <div
            class="segment-card filters-card"
            v-if="showFiltersCard"
          >
            <div class="segment-card-header">
              <div class="header-title flex-shrink-0 mr-4">
                <div class="d-flex">
                  <div class="total-audience">
                    <span class="far fa-users"></span>
                    {{ t('segments-form-audience') }}
                    <strong v-if="!loadingTarget" class="audience-count">{{
                      estimatedTarget
                    }}</strong>
                    <strong v-else>
                      <span class="far fa-spin fa-spinner"></span>
                    </strong>
                  </div>
                </div>
              </div>
              <div
                v-bind:class="
                  'ai-filter-generation ' + (usingAI ? 'active' : '')
                "
              >
                <input
                  ref="ai-input"
                  type="text"
                  class="form-control"
                  v-model="editedDescription"
                  @blur="
                    () => {
                      if (editedDescription.trim() === '') {
                        usingAI = false;
                      }
                    }
                  "
                  :placeholder="t('segments-form-ai_placeholder')"
                  id="ai-description"
                  name="ai-description"
                />
                <button
                  class="btn btn-ai flex-shrink-0"
                  :disabled="(usingAI && !editedDescription) || loadingFilters"
                  @click="useAI()"
                >
                  <span>{{ usingAI ? t('segments-form-ai_filter') : t('segments-form-ai_use') }}</span>
                </button>
              </div>
            </div>

            <div
              v-if="filterGenerationSuccess === false"
              class="alert alert-warning h-auto"
            >
              {{ t('segments-form-ai_error_prefix') }} <br />
              <strong>{{ t('segments-form-ai_error_hint') }}</strong> {{ filterGenerationMessage }}
            </div>

            <div v-if="segmentErrorMessage" class="alert alert-warning h-auto">
              {{ segmentErrorMessage }}
            </div>

            <div class="filter-section">
              <filter-group
                @change="changedFilters"
                ref="filter-bar"
                :update-query-string="false"
                :has-branch-filter="true"
                :has-period-filter="false"
                :is-vertical="true"
                :allow-filter-all-places="false"
                :show-labels="true"
                :enable-grouping="true"
                :group="loadingFilters ? emptyGroup : form.filters"
                :filter-options="extraFilters"
                :key="filterGroupKey"
              >
                <div slot="empty-state" class="d-flex justify-content-center">
                  <div v-if="loadingFilters" class="text-center w-100 d-flex flex-column align-items-center">

                    <sentence-swapper :sentences="[
                      t('segments-form-loading_sentences-s1'),
                      t('segments-form-loading_sentences-s2'),
                      t('segments-form-loading_sentences-s3'),
                      t('segments-form-loading_sentences-s4'),
                      t('segments-form-loading_sentences-s5'),
                      t('segments-form-loading_sentences-s6'),
                      t('segments-form-loading_sentences-s7'),
                      t('segments-form-loading_sentences-s8'),
                      t('segments-form-loading_sentences-s9'),
                      t('segments-form-loading_sentences-s10'),
                      t('segments-form-loading_sentences-s11')
                      ]" />
                    <div class="mt-4" style="width:80%; max-width: 400px;">
                      <ai-loader :height="12" :duration="30000" />
                    </div>

                  </div>
                  <div v-else class="text-center w-100 d-flex flex-column align-items-center">
                    <span class="text-lg">{{ t('segments-form-empty_state') }}</span>
                  </div>
                </div>
              </filter-group>
            </div>
            <div>
              <small>
                {{
                  loadingTarget
                    ? t('segments-form-calculating_target')
                    : segmentDescription
                }}
              </small>
            </div>
          </div>
          <div class="actions-container">
            <button
              class="btn btn-success btn-save-segment"
              :disabled="!isValid || savingSegment"
              @click.prevent="save"
            >
              <span v-if="!savingSegment">{{ t('segments-form-save') }}</span>
                <span v-else>
                    <i class="fas fa-spinner fa-spin"></i>
                    {{ id ? t('segments-form-updating') : t('segments-form-creating') }}
                </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
<script>
import keviApi from "../keviApi";
import "./register-filter-components";
import { FilterGroup } from "./register-filter-components";
import { mapState } from "vuex";
import { emptyGroup, buildSegmentExtraFilters } from "./segment-filter-options";
import SentenceSwapper from "../components/ui/sentence-swapper.vue";
export default {
  data: () => ({
    estimatedTarget: 0,
    segmentDescription: "",
    loadingTarget: false,
    state: "loading",
    id: null,
    form: {
      name: "",
      type: "dynamic",
      filters: { ...emptyGroup },
    },
    emptyGroup: { ...emptyGroup },
    fields: [],
    usingAI: false,
    generatingFilters: false,
    editingDescription: false,
    editedDescription: "",
    filterGenerationMessage: "",
    filterGenerationSuccess: null,
    loadingFilters: false,
    recentlyMounted: true,
    extraFilters: [],
    previousRequest: null,
    savingSegment: false,
    filterGroupKey: 0,
    populateFromFilters: false,
    errorReason: null,
  }),
  components: {
    SentenceSwapper,
    FilterGroup: FilterGroup,
  },
  computed: {
    ...mapState("placeDetails", ["users"]),
    placeId() {
      return this.$store.state.place.id;
    },
    isValid() {
      return this.form.name.length > 0;
    },
    segmentErrorMessage() {
      if (!this.errorReason) {
        return null;
      }

      return this.t(
        `segments-error-${this.errorReason}`,
        null,
        this.t("segments-error-generic"),
      );
    },
    place() {
      return this.$store.state.place;
    },
    user() {
      return this.$store.state.user;
    },
    isGlobalAdmin() {
      return this.user && this.user.global_admin;
    },
    isDynamic: {
      get() {
        return this.form.type === "dynamic";
      },
      set(value) {
        this.form.type = value ? "dynamic" : "manual";
      },
    },
    showFiltersCard() {
      if (this.form.type === "dynamic") {
        return true;
      }
      if (this.id) {
        return false;
      }
      return this.populateFromFilters;
    },
  },
  async mounted() {
    await this.loadFields();
    await this.resetFilters();
    await this.loadData();
  },
  methods: {
    t: function (term, replaces) {
      return this.$root.t(term, replaces);
    },
    useAI() {
      if (this.usingAI) {
        if (this.editedDescription.trim() === "") {
          return;
        }
        this.generateFilters();
      } else {
        this.usingAI = true;
        this.$nextTick(() => {
          this.$refs["ai-input"].focus();
        });
      }
    },
    async loadFields() {
      let options = keviApi.getOptions("/client-fields", "GET");
      const response = await this.$http(options);
      this.fields = JSON.parse(JSON.stringify(response.body));
    },
    is(state) {
      return this.state === state;
    },
    setState: function (state) {
      this.state = state;
    },
    save: function () {
      this.savingSegment = true;
      if (this.isValid) {
        let data = {
          id: this.id,
          name: this.form.name,
          type: this.form.type,
          filters: JSON.parse(JSON.stringify(this.form.filters)),
          populateFromFilters: this.form.type === "manual" ? this.populateFromFilters : false,
        };

        let options = keviApi.getOptions("segments/save", "POST", data);
        this.$http(options).then((response) => {
          if (response.status === 200) {
            window.mixpanel?.track(`${data.id ? "Update" : "Create"} Segment`, {
              segment_id: data.id,
              segment_name: data.name,
              segment_filters: data.filters,
              place_id: this.place.id,
              place_name: this.user.place_name,
              place_branch_name: this.user.branch_title,
              user_name: this.user.name,
              user_username: this.user.username,
              user_email: this.user.email,
            });
            this.$router.push("/segments");
          }
          this.savingSegment = false;
        }).catch((error) => {
          console.error(this.t("segments-form-save_error_log"), error);
          this.savingSegment = false;
        });
      } else {
        this.savingSegment = false;
      }
    },
    loadData() {
      this.id = null;
      if (this.$route.params.id) {
        this.setState("loading");

        let options = keviApi.getOptions("segment/" + this.$route.params.id);
        this.$http(options)
          .then((response) => {
            let result = response.body;
            this.id = result.id;
            this.errorReason = result.error_reason || null;
            this.form.name = result.name;
            this.form.type = result.type;
            this.form.filters = result.filters || {
              type: "group",
              operator: "AND",
              children: [],
            };

            this.estimateTarget(result.filters);
            this.resetFilters();
            this.setState("ready");
          })
          .catch((error) => {
            console.error("FORM: Error loading segment:", error);
            this.setState("ready");
          });
      } else {
        this.setState("ready");
      }
    },
    async resetFilters() {
      this.extraFilters = await buildSegmentExtraFilters(this);
    },
    estimateTarget(filters) {
      var options = keviApi.getOptions("segments/estimate-target", "GET", {
        filters,
      });
      options.before = (request) => {
        this.loadingTarget = true;
        if (this.previousRequest) {
          this.previousRequest.abort();
        }
        this.previousRequest = request;
      };

      this.$http(options)
        .then((response) => {
          this.estimatedTarget = response.body.target;
          this.segmentDescription = response.body.description;
          this.loadingTarget = false;
        })
        .catch((error) => {
          if (error.status !== 0) {
            this.loadingTarget = false;
          }
          console.error("Error estimating target:", error);
        });
    },
    generateFilters() {
      this.loadingFilters = true;
      this.filterGenerationMessage = "";
      this.filterGenerationSuccess = null;
      let options = keviApi.getOptions("segments/generate-filters", "POST", {
        description: this.editedDescription,
      });

      this.$http(options)
        .then((response) => {
          if (response.status === 200) {
            const result = response.body;

            if (result.success) {
              this.filterGenerationSuccess = true;
              this.filterGenerationMessage = this.t("segments-form-filters_generated_success");

              // Reset filter bar
              this.resetFilters();

              // Apply the new filters from API
              setTimeout(() => {
                this.changedFilters(result.filters);
                this.filterGroupKey++; // Força re-renderização do filter-group
                this.usingAI = false;
              }, 100);
            } else {
              this.filterGenerationSuccess = false;

              if (
                result.alternativeDescription &&
                result.alternativeDescription !== this.editedDescription
              ) {
                this.filterGenerationMessage = result.alternativeDescription;
              }
            }
          } else {
            this.filterGenerationSuccess = false;
            this.filterGenerationMessage = this.t("segments-form-filters_generated_error");
          }

          this.loadingFilters = false;
        })
        .catch((error) => {
          this.filterGenerationSuccess = false;
          this.filterGenerationMessage =
            this.t("segments-form-server_error_prefix") + " " + error.message;
          this.loadingFilters = false;
        });
    },
    changedFilters(filters) {
      if (!filters) {
        console.error(this.t("segments-form-invalid_filters_log"), filters);
        return;
      }
      this.form.filters = filters;
      this.estimateTarget(filters);
    },
  },
};
</script>
<style lang="scss">
/* Remover a importação que está causando erro */
/* @import url("../colors"); */

:root {
  --kevi-green: #11875a;
  --kevi-green-light: #e8f4ef;
  --kevi-gray-light: #f7f9fa;
  --kevi-red: #f44336;
  --kevi-green-dark: #0d6a44; /* Substituindo darken(#11875A, 5%) */
}

.segments-form-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 0;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #333;
}

.segment-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 24px;
  margin-bottom: 24px;

  h2 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #333;
  }

  small {
    display: block;
    color: #6c757d;
    margin-bottom: 16px;
    font-size: 14px;
  }
}

.segment-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  .header-title {
    display: flex;
    align-items: center;

    h2 {
      margin-bottom: 0;
      margin-right: 12px;
    }
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;

    .total-audience {
      margin-top: 16px;
    }
  }
}

.filter-badge {
  background-color: var(--kevi-green-light);
  color: var(--kevi-green);
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
}

.total-audience {
  background-color: var(--kevi-gray-light);
  border-radius: 6px;
  padding: 12px 16px;

  .audience-count {
    color: var(--kevi-green);
  }

  .fa-users {
    color: var(--kevi-green);
    margin-right: 8px;
  }
}

.filter-section {
  margin-top: 24px;
}

.segment-name {
  .input-group-text {
    background-color: var(--kevi-green-light);
    border-color: #ced4da;
    color: var(--kevi-green);
  }

  .form-control {
    border-color: #ced4da;

    &:focus {
      border-color: var(--kevi-green);
      box-shadow: 0 0 0 0.2rem rgba(17, 135, 90, 0.25);
    }
  }
}

.daily-update input {
  margin-top: 0;
}

.ai-filter-generation {
  border: 1px solid transparent;
  padding: 4px;
  border-radius: 8px;
  width: 100px;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  justify-content: flex-end;
  input {
    border: none;
    opacity: 0;
    position: absolute;
    &:focus {
      outline: none;
      box-shadow: none;
    }
  }
  &.active {
    border-color: #ddd;
    width: 100%;
    input {
      opacity: 1;
      visibility: visible;
      position: relative;
    }
  }
}

.update-option {
  .form-check-input {
    width: 3em;
    height: 1.5em;

    &:checked {
      background-color: var(--kevi-green);
      border-color: var(--kevi-green);
    }
  }

  .form-check-label {
    margin-left: 8px;
    font-size: 16px;
    font-weight: 500;
  }
}

.actions-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;

  .btn-save-segment {
    background-color: var(--kevi-green);
    border-color: var(--kevi-green);
    padding: 10px 24px;
    font-weight: 500;
    min-width: 120px;
    // min-width deixa o botao mais largo que o texto, entao o conteudo precisa
    // ser centralizado explicitamente.
    display: inline-flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 767px) {
      width: 100%;
    }

    &:disabled {
      opacity: 0.5;
    }

    &:hover,
    &:focus {
      background-color: var(--kevi-green-dark);
      border-color: var(--kevi-green-dark);
    }
  }
}

.segment-description {
  background-color: var(--kevi-gray-light);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;

  h3 {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .edit-button {
    color: var(--kevi-green);
    font-size: 13px;
    padding: 4px 8px;

    &:hover,
    &:focus {
      background-color: var(--kevi-green-light);
    }
  }

  .description-box {
    line-height: 1.5;
    color: #495057;
  }

  .description-edit {
    .form-control {
      margin-bottom: 16px;

      &:focus {
        border-color: var(--kevi-green);
        box-shadow: 0 0 0 0.2rem rgba(17, 135, 90, 0.25);
      }
    }

    .description-actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      margin-bottom: 16px;
    }

    .filter-message {
      padding: 12px;
      border-radius: 6px;
      font-size: 14px;
    }

    .filter-success {
      background-color: #d4edda;
      color: #155724;
    }

    .filter-error {
      background-color: #f8d7da;
      color: #721c24;
    }
  }
}

// Estilizando os filtros dentro do filter-bar
::v-deep .filter-group {
  .group-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
  }

  .operator-select {
    font-weight: 500;
    min-width: 220px;
  }

  .btn-add-item {
    font-size: 14px;
    border-color: var(--kevi-green);
    color: var(--kevi-green);

    &:hover,
    &:focus {
      background-color: var(--kevi-green);
      color: white;
    }
  }
}

::v-deep .filter-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  height: 100%;

  .filter-card-header {
    padding: 12px 16px;
    background-color: white;
    border-bottom: 1px solid #f0f0f0;

    .filter-name {
      font-weight: 600;
      font-size: 14px;
    }

    .btn-icon {
      &:hover,
      &:focus {
        color: var(--kevi-red);
      }
    }
  }

  .filter-card-body {
    padding: 16px;
  }
}
</style>
